import {ChangeDetectionStrategy, Component, effect, Injectable, input, output} from '@angular/core';
import {AksjeeierbokId, AksjeklasseId, AksjeklasseMedDetaljerModell, AksjeklasseRessursClient, HtmlTekst, LitenIkkeTomStreng, NyEllerOppdaterAksjeklasseSpesifikasjon, OppdaterAksjeklasserSpesifikansjonModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {FeltValidatorer} from "felles-ng2/infrastruktur/angular/feltValidatorer";
import {nyUuid} from "felles-ng2/støttefunksjoner/uuidGenerator";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatInputModule} from "@angular/material/input";
import {TranslateModule} from "@ngx-translate/core";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";
import {MatButtonModule} from "@angular/material/button";
import {MatIconModule} from "@angular/material/icon";
import {RiktekstRedigeringKomponent} from "web-ng2/delt/komponenter/skjema/riktekst-redigering/riktekst-redigering";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {asynkArbeidPågårSignal} from "felles-ng2/tjenester/AsynkArbeidPågårSignal";
import {MatTooltipModule} from "@angular/material/tooltip";
import {KortHandlingerKomponent} from "web-ng2/delt/komponenter/kort/kort-handlinger/kort-handlinger";
import {håndterRedigerListeEndringer} from "felles-ng2/infrastruktur/angular/redigerListeSkjema";

export type AksjeklasseSkjema = ReturnType<typeof AksjeklasseRedigerListeKomponent.opprettSkjemarad>
export type AksjeklasseSkjemaVerdier = AksjeklasseSkjema['value'];


@Component({
    selector: 'aksjeklasse-rediger-liste',
    templateUrl: './aksjeklasse-rediger-liste.html',
    styleUrls: ['./aksjeklasse-rediger-liste.scss'],
    imports: [
        ReactiveFormsModule,
        MatFormFieldModule,
        MatInputModule,
        TranslateModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        RiktekstRedigeringKomponent,
        KortHandlingerKomponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AksjeklasseRedigerListeKomponent {

    aksjeklasser = input.required<AksjeklasseMedDetaljerModell[]>();
    aksjeeierbokId = input.required<AksjeeierbokId>();

    vedFerdig = output<AksjeklasseMedDetaljerModell[] | null>();

    skjema = new FormArray<AksjeklasseSkjema>([]);
    lagrer = asynkArbeidPågårSignal();

    static opprettSkjemarad(verdier = {id: nyUuid(), tittel: '' as LitenIkkeTomStreng, beskrivelse: '' as HtmlTekst, ny: true, iBrukIBeholdning: false, arkivert: false}) {
        return new FormGroup({
            id: new FormControl<AksjeklasseId>(verdier.id, {nonNullable: true}),
            tittel: new FormControl<LitenIkkeTomStreng>(verdier.tittel, {nonNullable: true, validators: FeltValidatorer.litenStreng}),
            beskrivelse: new FormControl<HtmlTekst>(verdier.beskrivelse, {nonNullable: true, validators: FeltValidatorer.htmlTekst}),
            slettet: new FormControl<boolean>(false, {nonNullable: true}),
            iBrukIBeholdning: new FormControl<boolean>(verdier.iBrukIBeholdning, {nonNullable: true}),
            arkivert: new FormControl<boolean>(verdier.arkivert, {nonNullable: true}),
            ny: new FormControl<boolean>(verdier.ny, {nonNullable: true})
        });
    }

    constructor(private avhengigheter: AksjeklasseRedigerListeKomponentAvhengigheter) {
        this.skjema.valueChanges.pipe(takeUntilDestroyed())
          .subscribe(() => håndterRedigerListeEndringer(this.skjema, AksjeklasseRedigerListeKomponent.opprettSkjemarad));

        effect(() => {
            this.skjema.clear();
            this.aksjeklasser().forEach(k => {
                const rad = AksjeklasseRedigerListeKomponent.opprettSkjemarad({...k, tittel: k.navn, ny: false});
                rad.controls.tittel.addValidators(Validators.required);
                if (k.arkivert) rad.disable({emitEvent: false});
                this.skjema.push(rad, {emitEvent: false});
            });
            //sørger for at det blir lagt til en tom 'nytt innslag' rad
            håndterRedigerListeEndringer(this.skjema, AksjeklasseRedigerListeKomponent.opprettSkjemarad);
        });
    }

    async lagre() {
        const klasser = await this.lagrer.utfør(() =>
          this.avhengigheter.oppdater(this.aksjeeierbokId(), this.somOppdateringSpesifikasjon()));
        this.vedFerdig.emit(klasser);
    }

    avbryt() {
        this.vedFerdig.emit(null);
    }

    slett(rad: AksjeklasseSkjema, indeks: number) {
        if (rad.value.ny) {
            this.skjema.removeAt(indeks);
        } else {
            rad.disable();
            rad.patchValue({slettet: true});
        }
    }

    angreSlett(rad: AksjeklasseSkjema) {
        rad.enable();
        rad.patchValue({slettet: false});
    }

    arkiver(rad: AksjeklasseSkjema) {
        rad.disable();
        rad.patchValue({arkivert: true});
    }

    reaktiver(rad: AksjeklasseSkjema) {
        rad.patchValue({arkivert: false});
        rad.enable();
    }

    private somOppdateringSpesifikasjon(): OppdaterAksjeklasserSpesifikansjonModell {
        const oppdatering = {
            aksjeeierbokId: this.aksjeeierbokId(),
            slettes: [] as AksjeklasseId[],
            nye: [] as NyEllerOppdaterAksjeklasseSpesifikasjon[],
            oppdateres: [] as NyEllerOppdaterAksjeklasseSpesifikasjon[]
        };
        this.skjema.getRawValue().forEach(klasseSkjema => {
            if (klasseSkjema.ny) {
                if (erTomEllerNil(klasseSkjema.tittel)) return;
                oppdatering.nye.push({aksjeklasseId: klasseSkjema.id, beskrivelse: klasseSkjema.beskrivelse!, navn: klasseSkjema.tittel!, arkivert: klasseSkjema.arkivert});
            } else if (klasseSkjema.slettet) {
                oppdatering.slettes.push(klasseSkjema.id);
            } else {
                oppdatering.oppdateres.push({aksjeklasseId: klasseSkjema.id, beskrivelse: klasseSkjema.beskrivelse!, navn: klasseSkjema.tittel!, arkivert: klasseSkjema.arkivert});
            }
        });
        return oppdatering;
    }
}

@Injectable()
export class AksjeklasseRedigerListeKomponentAvhengigheter {

    constructor(private aksjeklasseRessursClient: AksjeklasseRessursClient) {
    }

    oppdater(aksjeeierbokId: AksjeeierbokId, oppdatering: OppdaterAksjeklasserSpesifikansjonModell): Promise<AksjeklasseMedDetaljerModell[]> {
        return this.aksjeklasseRessursClient.oppdater(aksjeeierbokId, oppdatering).somPromise();
    }

}
